import { useEffect, useLayoutEffect } from 'react';

import { Navigate, useLocation } from 'react-router-dom';

import { PageLoading } from '@/components/loading/PageLoading';
import { env } from '@/config/env';
import { PATHS } from '@/config/paths';
import { useGetCurrentUserQuery } from '@/features/auth/api/authApi';
import { logout, setCredentials, setUser } from '@/features/auth/stores/authSlice';
import { trySilentSessionRestore } from '@/features/auth/utils/silentSessionRestore';
import { isTokenExpired } from '@/features/auth/utils/token';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { baseApi } from '@/stores/api';

interface Props {
  children: React.ReactNode;
}

/**
 * Chỉ cho phép nội dung khi đã đăng nhập hợp lệ.
 * - JWT hết `exp` nhưng có API: thử refresh (cookie) trước khi đá về login.
 * - JWT hết `exp` và không có API: đăng xuất cục bộ.
 * - Có `VITE_API_BASE_URL`: xác thực phiên với GET `/api/auth/me` (401 → đăng xuất).
 */
export function ProtectedRoute({ children }: Props) {
  const { isAuthenticated, user, token } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const location = useLocation();

  const tokenExpired = token ? isTokenExpired(token) : true;
  const canRecoverExpiredSession = Boolean(env.API_BASE_URL);
  const recoverExpiredSession =
    isAuthenticated && !!token && tokenExpired && canRecoverExpiredSession;

  const shouldValidateWithApi =
    Boolean(env.API_BASE_URL) && isAuthenticated && !!token && !tokenExpired;

  const {
    data: meUser,
    isLoading,
    isError,
    error,
  } = useGetCurrentUserQuery(undefined, {
    skip: !shouldValidateWithApi,
    refetchOnMountOrArgChange: false,
  });

  useEffect(() => {
    if (!recoverExpiredSession) return;
    let cancelled = false;
    (async () => {
      const restored = await trySilentSessionRestore();
      if (cancelled) return;
      if (restored) {
        dispatch(setCredentials(restored));
      } else {
        dispatch(logout());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [recoverExpiredSession, dispatch]);

  useEffect(() => {
    if (token && tokenExpired && !canRecoverExpiredSession) {
      dispatch(logout());
    }
  }, [token, tokenExpired, canRecoverExpiredSession, dispatch]);

  useEffect(() => {
    if (isAuthenticated && !user) {
      dispatch(logout());
    }
  }, [isAuthenticated, user, dispatch]);

  useEffect(() => {
    if (meUser) {
      dispatch(setUser(meUser));
    }
  }, [meUser, dispatch]);

  const isUnauthorized =
    shouldValidateWithApi &&
    isError &&
    error &&
    typeof error === 'object' &&
    'status' in error &&
    (error.status as number) === 401;

  useLayoutEffect(() => {
    if (isUnauthorized) {
      dispatch(logout());
      dispatch(baseApi.util.invalidateTags(['Auth', 'User', 'Permissions']));
    }
  }, [isUnauthorized, dispatch]);

  if (!isAuthenticated) {
    return <Navigate to={PATHS.LOGIN} state={{ from: location }} replace />;
  }

  if (tokenExpired && !canRecoverExpiredSession) {
    return <Navigate to={PATHS.LOGIN} state={{ from: location }} replace />;
  }

  if (recoverExpiredSession) {
    return <PageLoading />;
  }

  if (shouldValidateWithApi && isLoading) {
    return <PageLoading />;
  }

  if (isUnauthorized) {
    return <PageLoading />;
  }

  return <>{children}</>;
}
